import Vue from "vue";

import { EventBus } from "@/eventBus";
import { SIP_STATUSES, PHONE_STATES } from "@/constants/phone";
import { createLogger } from "@/utils/logger";

require("@/lib/jssip.min.js");
require("@/utils/userMediaPolyfill");

const SESSION_EVENTS = ["progress", "failed", "ended", "confirmed"];
const logger = createLogger("telephony");

class TelephonyService {
  constructor() {
    this.events = new Vue();
    this.store = null;
    this.sipUA = null;
    this.sipSession = null;
    this.sipData = null;
    this.currentConfigKey = null;
    this.callState = null;
    this.isIncomingCall = false;
    this.registrationFallbackTimer = null;
    this.globalHandlersRegistered = false;
    this.windowHandlers = null;

    this.registerGlobalHandlers();
  }

  init({ store }) {
    if (!this.store) {
      this.store = store;
      this.syncStoreState();
    }
  }

  registerGlobalHandlers() {
    if (this.globalHandlersRegistered) {
      return;
    }

    this.globalHandlersRegistered = true;

    this.windowHandlers = {
      beforeUnload: () => {
        this.teardown("beforeunload");
      },
      pageHide: (event) => {
        if (event && event.persisted) {
          return;
        }

        this.teardown("pagehide");
      },
    };

    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", this.windowHandlers.beforeUnload);
      window.addEventListener("pagehide", this.windowHandlers.pageHide);
    }

    EventBus.$on("destroy-app-instance", () => {
      this.teardown("destroy_app_instance");
    });
    EventBus.$on("logout-user", () => {
      this.teardown("logout_user");
    });
  }

  on(event, handler) {
    this.events.$on(event, handler);
  }

  off(event, handler) {
    this.events.$off(event, handler);
  }

  emit(event, payload) {
    this.events.$emit(event, payload);
  }

  syncStoreState() {
    if (!this.store) {
      return;
    }

    this.store.commit("SET_PHONE_STATE_DATA", {
      status: this.callState,
      isRegistered: Boolean(this.sipUA && this.sipUA.isRegistered && this.sipUA.isRegistered()),
      isIncomingCall: this.isIncomingCall,
      hasSession: Boolean(this.sipSession),
    });
  }

  sanitizeCabinetLogToken(value) {
    if (value === null || typeof value === "undefined") {
      return null;
    }

    return String(value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || null;
  }

  getCabinetLogSnapshot() {
    return {
      callState: this.callState || null,
      hasConnection: Boolean(this.sipUA),
      hasSession: Boolean(this.sipSession),
      isIncomingCall: Boolean(this.isIncomingCall),
      isRegistered: Boolean(this.sipUA && this.sipUA.isRegistered && this.sipUA.isRegistered()),
      sipHost: this.sipData && this.sipData.sip_host ? this.sipData.sip_host : null,
    };
  }

  getCurrentOrderId() {
    return this.store?.state?.order?.order?.order?.id || null;
  }

  emitCabinetLog(eventName, payload = {}) {
    if (!eventName) {
      return;
    }

    EventBus.$emit("cabinet-log", {
      createdAt: new Date().toISOString(),
      eventName,
      orderId: this.getCurrentOrderId(),
      phoneState: this.callState || null,
      source: "telephony",
      telephony: this.getCabinetLogSnapshot(),
      ...payload,
    });
  }

  shouldLogTeardown() {
    return Boolean(
      this.sipSession
      || this.sipUA
      || this.currentConfigKey
      || this.callState
      || this.isIncomingCall
    );
  }

  clearRegistrationFallback() {
    clearTimeout(this.registrationFallbackTimer);
    this.registrationFallbackTimer = null;
  }

  scheduleRegistrationFallback() {
    this.clearRegistrationFallback();

    this.registrationFallbackTimer = setTimeout(() => {
      if (
        !this.sipUA
        || !this.sipUA.isConnected
        || !this.sipUA.isConnected()
        || !this.sipUA.register
        || (this.sipUA.isRegistered && this.sipUA.isRegistered())
      ) {
        return;
      }

      logger.warn("Registration fallback triggered");
      this.sipUA.register();
    }, 2500);
  }

  getSnapshot() {
    return {
      isRegistered: Boolean(this.sipUA && this.sipUA.isRegistered && this.sipUA.isRegistered()),
      hasConnection: Boolean(this.sipUA),
      hasSession: Boolean(this.sipSession),
      sipData: this.sipData,
      callState: this.callState,
      isIncomingCall: this.isIncomingCall,
    };
  }

  getSession() {
    return this.sipSession;
  }

  isActiveUa(ua) {
    return Boolean(ua) && ua === this.sipUA;
  }

  isActiveSession(session) {
    return Boolean(session) && session === this.sipSession;
  }

  buildConfigKey(sipData) {
    const { sip_login, sip_pass, sip_host } = sipData;
    return [sip_login, sip_pass, sip_host].join(":");
  }

  ensureInitialized(sipData) {
    if (!sipData) {
      return false;
    }

    const currentConfigKey = this.buildConfigKey(sipData);
    if (this.sipUA && this.currentConfigKey === currentConfigKey) {
      return true;
    }

    this.teardown("sip_reinit");

    const { sip_login, sip_pass, sip_host } = sipData;
    const login = `sip:webrtc_${sip_login}@${sip_host}`;
    const server = `wss://${sip_host}:8089/ws`;

    try {
      const socket = new window.JsSIP.WebSocketInterface(server);
      const configuration = {
        sockets: [socket],
        uri: login,
        password: sip_pass,
        register: true,
        stun_servers: ["stun.l.google.com:19302"],
        trace_sip: true,
        hack_via_ws: true,
      };

      this.sipUA = new window.JsSIP.UA(configuration);
      this.sipData = sipData;
      this.currentConfigKey = currentConfigKey;

      this.bindUaEvents();
      this.syncStoreState();
      this.sipUA.start();
      return true;
    } catch (error) {
      logger.error("Failed to initialize telephony", error);
      this.emit(SIP_STATUSES.REJECTED, error);
      return false;
    }
  }

  bindUaEvents() {
    if (!this.sipUA) {
      return;
    }

    const ua = this.sipUA;
    const {
      CONNECTED,
      DISCONNECTED,
      UNREGISTERED,
      REGISTERED,
      REGISTRATION_FAILED,
      FAILED,
    } = SIP_STATUSES;

    ua.on(CONNECTED, () => {
      if (!this.isActiveUa(ua)) {
        logger.debug("Ignoring stale connected event");
        return;
      }

      this.callState = CONNECTED;
      this.syncStoreState();
      this.scheduleRegistrationFallback();
      this.emit(CONNECTED);
    });

    ua.on(DISCONNECTED, () => {
      if (!this.isActiveUa(ua)) {
        logger.debug("Ignoring stale disconnected event");
        return;
      }

      this.clearRegistrationFallback();
      this.callState = DISCONNECTED;
      this.isIncomingCall = false;
      this.syncStoreState();
      this.emit(DISCONNECTED);
    });

    ua.on(UNREGISTERED, () => {
      if (!this.isActiveUa(ua)) {
        logger.debug("Ignoring stale unregistered event");
        return;
      }

      this.clearRegistrationFallback();
      this.callState = UNREGISTERED;
      this.isIncomingCall = false;
      this.syncStoreState();
      this.emit(UNREGISTERED);
    });

    ua.on(REGISTERED, () => {
      if (!this.isActiveUa(ua)) {
        logger.debug("Ignoring stale registered event");
        return;
      }

      this.clearRegistrationFallback();
      const hasActiveCall = [
        PHONE_STATES.RINGING,
        PHONE_STATES.DIALING,
        PHONE_STATES.CONVERSATION,
        PHONE_STATES.HANDING,
      ].includes(this.callState);

      if (!hasActiveCall) {
        this.callState = PHONE_STATES.READY;
      }
      this.syncStoreState();
      this.emit(REGISTERED);
    });

    ua.on(REGISTRATION_FAILED, (event) => {
      if (!this.isActiveUa(ua)) {
        logger.debug("Ignoring stale registrationFailed event");
        return;
      }

      this.clearRegistrationFallback();
      this.callState = REGISTRATION_FAILED;
      this.isIncomingCall = false;
      this.syncStoreState();
      this.emit(REGISTRATION_FAILED, event);
    });

    ua.on(FAILED, (event) => {
      if (!this.isActiveUa(ua)) {
        logger.debug("Ignoring stale failed event");
        return;
      }

      this.emit(FAILED, event);
    });

    ua.on("newRTCSession", (data) => {
      if (!this.isActiveUa(ua)) {
        logger.debug("Ignoring stale newRTCSession event");
        return;
      }

      this.sipSession = data.session;
      this.isIncomingCall = data.session.direction === "incoming";
      this.callState = this.isIncomingCall ? PHONE_STATES.RINGING : PHONE_STATES.DIALING;
      this.bindSessionEvents(data.session);
      this.syncStoreState();
      this.emit("newRTCSession", data);
    });
  }

  bindSessionEvents(session) {
    if (!session || session.__unicallBound) {
      return;
    }

    session.__unicallBound = true;

    SESSION_EVENTS.forEach((eventName) => {
      session.on(eventName, (event) => {
        if (!this.isActiveSession(session)) {
          logger.debug(`Ignoring stale session:${eventName} event`);
          return;
        }

        if (eventName === "failed" || eventName === "ended") {
          if (this.sipSession === session) {
            this.sipSession = null;
          }
          this.isIncomingCall = false;
          this.callState = this.sipUA && this.sipUA.isRegistered && this.sipUA.isRegistered()
            ? PHONE_STATES.READY
            : null;
        }

        if (eventName === "confirmed") {
          this.isIncomingCall = false;
          this.callState = PHONE_STATES.CONVERSATION;
        }

        this.syncStoreState();
        const payload = event || {};
        payload.sessionDirection = session.direction || null;
        payload.sessionId = session.id || null;
        this.emit(`session:${eventName}`, payload);
      });
    });
  }

  call(target, options) {
    if (!this.sipUA) {
      return null;
    }

    const hasActiveSession = Boolean(this.sipSession);
    const isBusyState = [
      PHONE_STATES.RINGING,
      PHONE_STATES.DIALING,
      PHONE_STATES.CONVERSATION,
      PHONE_STATES.HANDING,
    ].includes(this.callState);

    if (hasActiveSession || isBusyState) {
      logger.warn("Ignoring outbound call attempt while another session is active");
      return null;
    }

    this.sipSession = this.sipUA.call(target, options);
    this.isIncomingCall = false;
    this.callState = PHONE_STATES.DIALING;
    this.bindSessionEvents(this.sipSession);
    this.syncStoreState();
    return this.sipSession;
  }

  register() {
    if (!this.sipUA) {
      return;
    }

    this.clearRegistrationFallback();
    this.sipUA.register();
  }

  unregister() {
    if (!this.sipUA) {
      return;
    }

    this.sipUA.unregister();
  }

  terminateSessions() {
    if (!this.sipUA) {
      return;
    }

    this.callState = PHONE_STATES.HANDING;
    this.sipUA.terminateSessions();
    this.syncStoreState();
  }

  terminateCurrentSession() {
    if (!this.sipSession) {
      return;
    }

    this.callState = PHONE_STATES.HANDING;
    this.sipSession.terminate();
    this.syncStoreState();
  }

  answer(options) {
    if (!this.sipSession) {
      return;
    }

    this.isIncomingCall = false;
    this.callState = PHONE_STATES.CONVERSATION;
    this.sipSession.answer(options);
    this.syncStoreState();
  }

  sendDTMF(value) {
    if (!this.sipSession) {
      return;
    }

    this.sipSession.sendDTMF(value);
  }

  hold() {
    if (!this.sipSession) {
      return;
    }

    this.sipSession.hold();
  }

  unhold() {
    if (!this.sipSession) {
      return;
    }

    this.sipSession.unhold();
  }

  mute() {
    if (!this.sipSession) {
      return;
    }

    this.sipSession.mute();
  }

  unmute() {
    if (!this.sipSession) {
      return;
    }

    this.sipSession.unmute();
  }

  teardown(reason = "unknown", payload = {}) {
    const normalizedReason = this.sanitizeCabinetLogToken(reason) || "unknown";

    if (this.shouldLogTeardown()) {
      this.emitCabinetLog(`phone.teardown.${normalizedReason}`, {
        teardown: {
          hasConnection: Boolean(this.sipUA),
          hasSession: Boolean(this.sipSession),
          isIncomingCall: Boolean(this.isIncomingCall),
          reason: normalizedReason,
          ...payload,
        },
      });
    }

    this.clearRegistrationFallback();

    if (this.sipSession) {
      try {
        this.sipSession.terminate();
      } catch (error) {
        // noop
      }
      this.sipSession = null;
    }

    if (this.sipUA) {
      try {
        this.sipUA.terminateSessions();
      } catch (error) {
        // noop
      }

      try {
        this.sipUA.unregister();
      } catch (error) {
        // noop
      }

      try {
        this.sipUA.stop();
      } catch (error) {
        // noop
      }
    }

    this.sipUA = null;
    this.sipData = null;
    this.currentConfigKey = null;
    this.callState = null;
    this.isIncomingCall = false;
    this.syncStoreState();
  }
}

export default new TelephonyService();
